import { useState, useEffect } from 'react';
import { adminService } from '@/api/services/admin';
import type { User } from '@/api/services/admin';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { UserDrawer } from '@/components/UserDrawer';

// ── UserRow ───────────────────────────────────────────────────────────────────

interface UserRowProps {
  user: User;
  onChange: () => void;
  onOpenDrawer: (user: User) => void;
}

function UserRow({ user, onChange, onOpenDrawer }: UserRowProps) {
  const [toggling, setToggling] = useState(false);
  const [addingCredits, setAddingCredits] = useState(false);
  const [creditsValue, setCreditsValue] = useState(15);
  const [actionError, setActionError] = useState('');

  const handleToggle = async () => {
    if (user.active) {
      if (!window.confirm(`Disable ${user.email}?`)) return;
    }
    setActionError('');
    setToggling(true);
    try {
      if (user.active) {
        await adminService.disableUser(user.id);
      } else {
        await adminService.enableUser(user.id);
      }
      onChange();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed.');
    } finally {
      setToggling(false);
    }
  };

  const handleAddCredits = async () => {
    const n = creditsValue;
    if (!n || n < 1) {
      setActionError('Credits must be at least 1.');
      return;
    }
    setActionError('');
    setAddingCredits(true);
    try {
      await adminService.addCredits(user.id, n);
      setCreditsValue(15);
      onChange();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to add credits.');
    } finally {
      setAddingCredits(false);
    }
  };

  const busy = toggling || addingCredits;

  return (
    <tr className="border-b border-border last:border-0 hover:bg-surface/50 transition-colors">
      {/* User — name is clickable to open drawer */}
      <td className="px-4 py-3 align-top">
        <button
          className="text-left w-full focus:outline-none"
          onClick={(e) => { e.stopPropagation(); onOpenDrawer(user); }}
        >
          <p className="text-sm font-medium text-primary-500 dark:text-primary-400 hover:underline leading-snug">
            {user.full_name || <span className="italic text-text-muted">No name</span>}
          </p>
          <p className="text-xs text-text-secondary mt-0.5">{user.email}</p>
        </button>
      </td>

      {/* Org */}
      <td className="px-4 py-3 align-top">
        <span className="text-sm text-text-secondary">{user.org_name || '—'}</span>
      </td>

      {/* Active */}
      <td className="px-4 py-3 align-top">
        <Badge variant={user.active ? 'success' : 'error'} size="xs">
          {user.active ? 'Active' : 'Disabled'}
        </Badge>
      </td>

      {/* Credits */}
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-text-primary tabular-nums">
            {user.status.subscription.available_credits.toLocaleString()}
          </span>
          {user.status.subscription.is_subscribed && (
            <Badge variant="primary" size="xs">Sub</Badge>
          )}
        </div>
      </td>

      {/* Counters */}
      <td className="px-4 py-3 align-top">
        <p className="text-xs text-text-secondary whitespace-nowrap">
          FA {user.status.fa_assessment}/{user.status.fa_analysis}
          {' · '}Search {user.status.prospect_search}
          {' · '}Research {user.status.prospect_research}
          {' · '}Msg {user.status.prospect_messages}
        </p>
      </td>

      {/* Actions */}
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle */}
          <Button
            variant={user.active ? 'danger' : 'secondary'}
            size="sm"
            disabled={busy}
            onClick={handleToggle}
          >
            {toggling ? '...' : user.active ? 'Disable' : 'Enable'}
          </Button>

          {/* Add credits inline */}
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              value={creditsValue}
              disabled={busy}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                setCreditsValue(Number.isFinite(n) ? n : 0);
              }}
              className="w-16 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={handleAddCredits}
            >
              {addingCredits ? 'Adding...' : '+ Credits'}
            </Button>
          </div>

          {/* Inline action error */}
          {actionError && (
            <span className="text-xs text-error-700 dark:text-error-400">{actionError}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Users page ────────────────────────────────────────────────────────────────

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [drawerUser, setDrawerUser] = useState<User | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchUsers = () => {
    setLoading(true);
    setError('');
    adminService
      .getUsers()
      .then((data) => setUsers(data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load users.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenDrawer = (user: User) => {
    setDrawerUser(user);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter(
        (u) =>
          u.full_name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.org_name.toLowerCase().includes(q),
      )
    : users;

  return (
    <div className="space-y-4">
      {/* Header */}
      <h1 className="text-2xl font-semibold text-text-primary">
        Users{' '}
        <span className="text-text-secondary text-base font-normal">({users.length})</span>
      </h1>

      {/* Search */}
      <div className="max-w-sm">
        <Input
          placeholder="Search by name, email, or org…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 p-3 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
          <p className="text-sm text-error-700 dark:text-error-400 flex-1">{error}</p>
          <Button variant="secondary" size="sm" onClick={fetchUsers}>
            Retry
          </Button>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-border bg-surface-elevated">
                <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">User</th>
                <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Org</th>
                <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Active</th>
                <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Credits</th>
                <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Counters</th>
                <th className="px-4 py-2.5 text-xs font-medium text-text-muted uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-text-secondary">
                    {users.length === 0
                      ? 'No users yet.'
                      : `No users match "${query}"`}
                  </td>
                </tr>
              ) : (
                filtered.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    onChange={fetchUsers}
                    onOpenDrawer={handleOpenDrawer}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* User drawer */}
      <UserDrawer
        user={drawerUser}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onChange={fetchUsers}
      />
    </div>
  );
}
